import { useState } from 'react';
import { ChevronDownIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../Avatar';
import { formatRelative, formatShortDate } from '../patient-files/timeline-rail/event-time';
import type { ChargeEventItem } from '../../services/billing';
import { billingEventStyle } from './billingEventDisplay';

// Audit-trail timeline for billing entities (charge and invoice drawers),
// fed by billing.billing_event rows and rendered in the same visual language
// as the patient timeline rail: an 18px toned dot on a hairline rail, one
// bordered white card per event with a click-to-expand payload body and an
// actor footer. Tone class strings mirror timeline-rail/event-config.
//
// Naming an entry needs entityType and payload.action as well as eventType -
// see billingEventDisplay.tsx, which owns that resolution.

interface ActorDisplay {
    name: string;
    isHuman: boolean;
}

function actorDisplay(event: ChargeEventItem): ActorDisplay {
    const isHuman = event.actor.startsWith('user:');
    if (event.actorName) return { name: event.actorName, isHuman };
    if (event.actor === 'system') return { name: 'System', isHuman: false };
    if (event.actor.startsWith('cron:'))
        return { name: `Cron · ${event.actor.slice('cron:'.length)}`, isHuman: false };
    if (isHuman) return { name: 'User', isHuman: true };
    return { name: event.actor, isHuman: false };
}

// Flat string/number/boolean payloads render as key-value rows; anything
// nested falls back to its JSON text on the value side.
function payloadEntries(payload: unknown): [string, string][] | null {
    if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) return null;
    return Object.entries(payload as Record<string, unknown>).map(([key, value]) => [
        key,
        typeof value === 'object' ? JSON.stringify(value) : String(value)
    ]);
}

function PayloadBody({ payload }: { payload: unknown }) {
    const entries = payloadEntries(payload);
    if (entries === null) {
        return (
            <pre className="bg-gray-50 rounded-lg p-2.5 text-[11px] font-mono text-gray-600 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(payload, null, 2)}
            </pre>
        );
    }
    return (
        <div className="flex flex-col gap-1">
            {entries.map(([key, value]) => (
                <div key={key} className="flex items-start justify-between gap-3">
                    <span className="text-[11px] text-gray-400 shrink-0">{key}</span>
                    <span className="text-xs text-gray-700 font-medium text-right break-all">
                        {value}
                    </span>
                </div>
            ))}
        </div>
    );
}

const ACTOR_AVATAR_SIZE_PX = 16;

function ActorFooter({ event }: { event: ChargeEventItem }) {
    const actor = actorDisplay(event);
    const shortDate = formatShortDate(event.occurredAt);
    return (
        <div className="flex items-center gap-1.5 mt-1.5">
            {actor.isHuman ? (
                <div
                    className="flex-shrink-0"
                    style={{ width: ACTOR_AVATAR_SIZE_PX, height: ACTOR_AVATAR_SIZE_PX }}
                >
                    <Avatar user={{ name: actor.name, email: '' }} />
                </div>
            ) : (
                <span className="w-4 h-4 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-400 flex-shrink-0">
                    <Cog6ToothIcon className="w-2.5 h-2.5" />
                </span>
            )}
            <span className="text-[11px] text-gray-500 truncate flex-1 capitalize">
                {actor.name}
            </span>
            {shortDate && <span className="text-[10px] text-gray-300">{shortDate}</span>}
        </div>
    );
}

interface EventCardProps {
    event: ChargeEventItem;
    isLast: boolean;
}

function EventCard({ event, isLast }: EventCardProps) {
    const style = billingEventStyle(event);
    const Icon = style.icon;
    const entries = payloadEntries(event.payload);
    const expandable = event.payload != null && (entries === null || entries.length > 0);
    const [expanded, setExpanded] = useState(false);
    const relative = formatRelative(event.occurredAt);

    return (
        <li className="relative pl-7 pb-2.5 last:pb-0">
            {!isLast && <span className="absolute left-[10px] top-5 bottom-0 w-px bg-gray-200" />}
            <span
                className={`absolute left-[2px] top-1.5 w-[18px] h-[18px] rounded-full border ${style.tone.ring} flex items-center justify-center ${style.tone.icon}`}
            >
                <Icon className="w-3 h-3" />
            </span>
            <div
                className={`rounded-lg border border-gray-200 bg-white px-2.5 py-2 font-inter transition-colors ${
                    expandable ? 'hover:border-gray-300' : ''
                }`}
            >
                {/* Only the header toggles — the expanded payload stays a plain,
            selectable region so values can be copied without collapsing. */}
                <div
                    className={`flex items-baseline justify-between gap-2 ${expandable ? 'cursor-pointer select-none' : ''}`}
                    onClick={expandable ? () => setExpanded((v) => !v) : undefined}
                    onKeyDown={
                        expandable
                            ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setExpanded((v) => !v);
                                  }
                              }
                            : undefined
                    }
                    role={expandable ? 'button' : undefined}
                    tabIndex={expandable ? 0 : undefined}
                    aria-expanded={expandable ? expanded : undefined}
                >
                    <span className="text-xs font-semibold text-gray-900 leading-tight truncate">
                        {style.label}
                    </span>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        {relative && <span className="text-[10px] text-gray-400">{relative}</span>}
                        {expandable && (
                            <ChevronDownIcon
                                className={`w-3 h-3 text-gray-400 transition-transform duration-150 ${expanded ? 'rotate-180' : ''}`}
                            />
                        )}
                    </div>
                </div>

                {expandable && expanded && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                        <PayloadBody payload={event.payload} />
                    </div>
                )}

                <ActorFooter event={event} />
            </div>
        </li>
    );
}

export function EventTimeline({ events }: { events: ChargeEventItem[] }) {
    if (events.length === 0) {
        return <p className="text-sm text-gray-400">No activity recorded.</p>;
    }
    return (
        <ul className="flex flex-col">
            {events.map((event, i) => (
                <EventCard key={event.id} event={event} isLast={i === events.length - 1} />
            ))}
        </ul>
    );
}
